<script setup lang="ts">
import { InputType } from '@/interfaces/InputFields';
import { computed } from 'vue';
import ConnxButton from '@/components/Atoms/ConnxButton.vue';
import ConnxField from '@/components/Molecules/Field/ConnxField.vue';
import ConnxIcon from '@/components/Atoms/ConnxIcon.vue';

const props = defineProps<{
  disabled?: boolean;
  showRawHtml?: boolean;
  isActive: (type: string) => boolean;
  isFullscreen?: boolean;
}>();

const emit = defineEmits<{
  (e: 'action', action: string, value?: any): void;
}>();

// Emitters
const toggleBold = () => emit('action', 'toggleBold');
const toggleItalic = () => emit('action', 'toggleItalic');
const toggleStrike = () => emit('action', 'toggleStrike');
const toggleUnderline = () => emit('action', 'toggleUnderline');
const alignLeft = () => emit('action', 'alignLeft');
const alignCenter = () => emit('action', 'alignCenter');
const alignRight = () => emit('action', 'alignRight');
const toggleBulletList = () => emit('action', 'toggleBulletList');
const toggleOrderedList = () => emit('action', 'toggleOrderedList');
const toggleBlockquote = () => emit('action', 'toggleBlockquote');
const toggleCodeBlock = () => emit('action', 'toggleCodeBlock');
const setLink = () => emit('action', 'setLink');
const unsetLink = () => emit('action', 'unsetLink');
const undo = () => emit('action', 'undo');
const redo = () => emit('action', 'redo');
const toggleRawHtml = () => emit('action', 'toggleRawHtml');
const toggleFullscreen = () => emit('action', 'toggleFullscreen');

const selectedHeading = computed({
  get() {
    if (props.isActive('heading1')) return '1';
    if (props.isActive('heading2')) return '2';
    if (props.isActive('heading3')) return '3';
    if (props.isActive('heading4')) return '4';
    if (props.isActive('heading5')) return '5';
    if (props.isActive('heading6')) return '6';
    return '0';
  },
  set(newValue) {
    emit('action', 'heading', newValue);
  },
});
</script>

<template>
  <div
    class="tw-bg-connx-white tw-flex tw-flex-wrap tw-gap-2 tw-items-stretch tw-border tw-border-connx-grey tw-rounded tw-relative"
  >
    <!-- Left side buttons -->
    <div class="tw-flex tw-flex-wrap tw-gap-2 tw-items-center tw-flex-1">
      <!-- Bold -->
      <slot>
        <connx-button
          :disabled="showRawHtml || disabled"
          @click="toggleBold"
          :class="{ 'Connx-btn--active': isActive('bold') }"
          button-style="icon"
          button-size="sm"
          class="hover:tw-bg-connx-primary hover:tw-text-connx-white hover:tw-border-connx-primary"
          title="Bold"
        >
          <connx-icon name="fal fa-bold" class="tw-h-4"></connx-icon>
        </connx-button>

        <!-- Italic -->
        <connx-button
          :disabled="showRawHtml || disabled"
          @click="toggleItalic"
          :class="{ 'Connx-btn--active': isActive('italic') }"
          button-style="icon"
          button-size="sm"
          class="hover:tw-bg-connx-primary hover:tw-text-connx-white hover:tw-border-connx-primary"
          title="Italic"
        >
          <connx-icon name="fal fa-italic" class="tw-h-4"></connx-icon>
        </connx-button>

        <!-- Strike -->
        <connx-button
          :disabled="showRawHtml || disabled"
          @click="toggleStrike"
          :class="{ 'Connx-btn--active': isActive('strike') }"
          button-style="icon"
          button-size="sm"
          class="hover:tw-bg-connx-primary hover:tw-text-connx-white hover:tw-border-connx-primary"
          title="Strikethrough"
        >
          <connx-icon name="fal fa-strikethrough" class="tw-h-4"></connx-icon>
        </connx-button>

        <!-- Underline -->
        <connx-button
          :disabled="showRawHtml || disabled"
          @click="toggleUnderline"
          :class="{ 'Connx-btn--active': isActive('underline') }"
          button-style="icon"
          button-size="sm"
          class="hover:tw-bg-connx-primary hover:tw-text-connx-white hover:tw-border-connx-primary"
          title="Underline"
        >
          <connx-icon name="fal fa-underline" class="tw-h-4"></connx-icon>
        </connx-button>

        <!-- Headings -->
        <connx-field
          :disabled="showRawHtml || disabled"
          :type="InputType.Select"
          class="tw-mb-0 tw-h-9"
          name="headings"
          v-model="selectedHeading"
          :options="[
            { label: 'Paragraph', value: '0' },
            { label: 'Heading 1', value: '1' },
            { label: 'Heading 2', value: '2' },
            { label: 'Heading 3', value: '3' },
            { label: 'Heading 4', value: '4' },
            { label: 'Heading 5', value: '5' },
            { label: 'Heading 6', value: '6' },
          ]"
        />

        <!-- Alignment: Left -->
        <connx-button
          :disabled="showRawHtml || disabled"
          @click="alignLeft"
          :class="{ 'Connx-btn--active': isActive('alignLeft') }"
          button-style="icon"
          button-size="sm"
          class="hover:tw-bg-connx-primary hover:tw-text-connx-white hover:tw-border-connx-primary"
          title="Align Left"
        >
          <connx-icon name="fal fa-align-left" class="tw-h-4"></connx-icon>
        </connx-button>

        <!-- Alignment: Center -->
        <connx-button
          :disabled="showRawHtml || disabled"
          @click="alignCenter"
          :class="{ 'Connx-btn--active': isActive('alignCenter') }"
          button-style="icon"
          button-size="sm"
          class="hover:tw-bg-connx-primary hover:tw-text-connx-white hover:tw-border-connx-primary"
          title="Align Center"
        >
          <connx-icon name="fal fa-align-center" class="tw-h-4"></connx-icon>
        </connx-button>

        <!-- Alignment: Right -->
        <connx-button
          :disabled="showRawHtml || disabled"
          @click="alignRight"
          :class="{ 'Connx-btn--active': isActive('alignRight') }"
          button-style="icon"
          button-size="sm"
          class="hover:tw-bg-connx-primary hover:tw-text-connx-white hover:tw-border-connx-primary"
          title="Align Right"
        >
          <connx-icon name="fal fa-align-right" class="tw-h-4"></connx-icon>
        </connx-button>

        <!-- Bullet List -->
        <connx-button
          :disabled="showRawHtml || disabled"
          @click="toggleBulletList"
          :class="{ 'Connx-btn--active': isActive('bulletList') }"
          button-style="icon"
          button-size="sm"
          class="hover:tw-bg-connx-primary hover:tw-text-connx-white hover:tw-border-connx-primary"
          title="Bullet List"
        >
          <connx-icon name="fal fa-list-ul" class="tw-h-4"></connx-icon>
        </connx-button>

        <!-- Ordered List -->
        <connx-button
          :disabled="showRawHtml || disabled"
          @click="toggleOrderedList"
          :class="{ 'Connx-btn--active': isActive('orderedList') }"
          button-style="icon"
          button-size="sm"
          class="hover:tw-bg-connx-primary hover:tw-text-connx-white hover:tw-border-connx-primary"
          title="Numbered List"
        >
          <connx-icon name="fal fa-list-ol" class="tw-h-4"></connx-icon>
        </connx-button>

        <!-- Blockquote -->
        <connx-button
          :disabled="showRawHtml || disabled"
          @click="toggleBlockquote"
          :class="{ 'Connx-btn--active': isActive('blockquote') }"
          button-style="icon"
          button-size="sm"
          class="hover:tw-bg-connx-primary hover:tw-text-connx-white hover:tw-border-connx-primary"
          title="Quote"
        >
          <connx-icon name="fal fa-quote-right" class="tw-h-4"></connx-icon>
        </connx-button>

        <!-- Code Block -->
        <connx-button
          :disabled="showRawHtml || disabled"
          @click="toggleCodeBlock"
          :class="{ 'Connx-btn--active': isActive('codeBlock') }"
          button-style="icon"
          button-size="sm"
          class="hover:tw-bg-connx-primary hover:tw-text-connx-white hover:tw-border-connx-primary"
          title="Code Block"
        >
          <connx-icon name="fal fa-file-code" class="tw-h-4"></connx-icon>
        </connx-button>

        <!-- Link -->
        <connx-button
          :disabled="showRawHtml || disabled"
          @click="setLink"
          :class="{ 'Connx-btn--active': isActive('link') }"
          button-style="icon"
          button-size="sm"
          class="hover:tw-bg-connx-primary hover:tw-text-connx-white hover:tw-border-connx-primary"
          title="Insert Link"
        >
          <connx-icon name="fal fa-link" class="tw-h-4"></connx-icon>
        </connx-button>

        <!-- Unlink -->
        <connx-button
          :disabled="showRawHtml || disabled"
          @click="unsetLink"
          button-style="icon"
          button-size="sm"
          class="hover:tw-bg-connx-primary hover:tw-text-connx-white hover:tw-border-connx-primary"
          title="Remove Link"
        >
          <connx-icon name="fal fa-unlink" class="tw-h-4"></connx-icon>
        </connx-button>

        <!-- Undo -->
        <connx-button
          :disabled="showRawHtml || disabled"
          @click="undo"
          button-style="icon"
          button-size="sm"
          class="hover:tw-bg-connx-primary hover:tw-text-connx-white hover:tw-border-connx-primary"
          title="Undo"
        >
          <connx-icon name="fal fa-undo" class="tw-h-4"></connx-icon>
        </connx-button>

        <!-- Redo -->
        <connx-button
          :disabled="showRawHtml || disabled"
          @click="redo"
          button-style="icon"
          button-size="sm"
          class="hover:tw-bg-connx-primary hover:tw-text-connx-white hover:tw-border-connx-primary"
          title="Redo"
        >
          <connx-icon name="fal fa-redo" class="tw-h-4"></connx-icon>
        </connx-button>

        <!-- Raw HTML Toggle -->
        <connx-button
          @click="toggleRawHtml"
          button-style="icon"
          button-size="sm"
          :disabled="disabled"
          :class="{ 'Connx-btn--active': showRawHtml }"
          class="hover:tw-bg-connx-primary hover:tw-text-connx-white hover:tw-border-connx-primary"
          :title="showRawHtml ? 'Switch to Editor' : 'Switch to Raw HTML'"
        >
          <connx-icon name="fal fa-code" class="tw-h-4"></connx-icon>
        </connx-button>
      </slot>
      <connx-button
        @click="toggleFullscreen"
        v-if="!isFullscreen"
        button-style="icon"
        :disabled="disabled"
        button-size="sm"
        :class="{ 'Connx-btn--active': isFullscreen }"
        class="hover:tw-bg-connx-primary hover:tw-text-connx-white hover:tw-border-connx-primary"
        :title="isFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen'"
      >
        <connx-icon :name="isFullscreen ? 'fal fa-compress' : 'fal fa-expand'" class="tw-h-4"></connx-icon>
      </connx-button>
    </div>

    <!-- Fullscreen button - positioned on the right -->
    <div class="tw-flex tw-items-center" v-if="isFullscreen">
      <connx-button
        @click="toggleFullscreen"
        :disabled="disabled"
        button-style="icon"
        button-size="sm"
        :class="{ 'Connx-btn--active': isFullscreen }"
        class="hover:tw-bg-connx-primary hover:tw-text-connx-white hover:tw-border-connx-primary"
        :title="isFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen'"
      >
        <connx-icon :name="isFullscreen ? 'fal fa-compress' : 'fal fa-expand'" class="tw-h-4"></connx-icon>
      </connx-button>
    </div>
  </div>
</template>
